import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mediaService, type MediaAsset } from '../../services/studio.service';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { fmtBytes } from '../landing/format';
import { FONT_SANS, FONT_SERIF } from '../landing/landingData';
import { Dots, FillButton, FilterDropdown, ICON, Icon, PageHead, SearchBox, ViewToggle, useDash, useDismiss, type Layout } from './dashUi';

/**
 * Dashboard Media (design "Media", internally "library"): the user's uploaded
 * images and videos, reusable in any site or video. Grid or list (remembered),
 * type filter, search, full-screen preview with prev/next, download, delete.
 * The backend only stores images and videos, so the design's "Docs" type is left out.
 */

type KindFilter = 'all' | 'image' | 'video';
const KIND_OPTS: Array<[KindFilter, string]> = [['all', 'All'], ['image', 'Images'], ['video', 'Videos']];
const LAYOUT_KEY = 'hyvay:dash-media-layout';
const PAGE = 8;

interface Upload { id: number; name: string; progress: number }

const readLayout = (): Layout => {
    try { return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'grid'; } catch { return 'grid'; }
};
const kindWord = (a: MediaAsset) => (a.kind === 'video' ? 'Video' : 'Image');
const metaOf = (a: MediaAsset) => `${kindWord(a)} · ${fmtBytes(a.bytes)}`.toUpperCase();
/** ApiResponse is a union; only its failure branch carries `message`. */
const errMsg = (r: unknown) => (r as { message?: string }).message;
const absUrl = (u: string) => { try { return new URL(u, window.location.origin).href; } catch { return u; } };

const menuItem: React.CSSProperties = { width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', border: 0, borderRadius: 5, background: 'transparent', fontSize: 13, fontWeight: 500, color: 'var(--hv-ink)', cursor: 'pointer', textAlign: 'left', textDecoration: 'none' };
const dotsBtn: React.CSSProperties = { flex: 'none', borderRadius: 8, border: 0, background: 'transparent', color: 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const pvBtn: React.CSSProperties = { flex: 'none', width: 36, height: 36, borderRadius: 999, border: 0, background: 'rgba(255,255,255,0.08)', color: '#F3F2EC', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', textDecoration: 'none' };
const listCols = 'minmax(200px,2.4fr) minmax(110px,1fr) minmax(110px,1fr) 40px';

const DownloadIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 4v11M7.5 10.5L12 15l4.5-4.5M5 19.5h14" /></svg>;
const LinkIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" /></svg>;
const EyeIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z M12 14.8a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6z" /></svg>;

/** "..." menu of one file. */
const FileMenu: React.FC<{ a: MediaAsset; style: React.CSSProperties; onClose: () => void; onPreview: () => void; onCopy: () => void; onDelete: () => void }> = ({ a, style, onClose, onPreview, onCopy, onDelete }) => {
    const ref = useDismiss<HTMLDivElement>(true, onClose);
    return (
        <div ref={ref} data-hv-menu="1" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', zIndex: 31, width: 180, padding: 6, borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 20px 40px -18px var(--hv-shadow)', animation: 'hvPop 0.18s ease both', ...style }}>
            <button type="button" className="hv-mi" style={menuItem} onClick={() => { onClose(); onPreview(); }}><EyeIcon />Preview</button>
            <button type="button" className="hv-mi" style={menuItem} onClick={() => { onClose(); onCopy(); }}><LinkIcon />Copy link</button>
            <a href={a.url} target="_blank" rel="noopener noreferrer" download={a.originalName} className="hv-mi" style={menuItem} onClick={onClose}><DownloadIcon />Download</a>
            <button type="button" className="hv-mi-danger" style={{ ...menuItem, color: 'var(--hv-danger)' }} onClick={() => { onClose(); onDelete(); }}><Icon d={ICON.trash} size={15} />Delete</button>
        </div>
    );
};

/** Full-screen lightbox (design pv*): image or playable video, prev/next over the filtered list. */
const Preview: React.FC<{ list: MediaAsset[]; index: number; onGo: (i: number) => void; onClose: () => void; onDelete: (a: MediaAsset) => void }> = ({ list, index, onGo, onClose, onDelete }) => {
    const a = list[index];
    const go = useCallback((d: number) => { if (list.length) onGo((index + d + list.length) % list.length); }, [index, list.length, onGo]);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight') go(1);
            else if (e.key === 'ArrowLeft') go(-1);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [go, onClose]);
    if (!a) return null;
    return (
        <div role="dialog" aria-modal="true" aria-label="File preview" style={{ position: 'fixed', inset: 0, zIndex: 8800, display: 'flex', flexDirection: 'column', background: 'rgba(12,16,14,0.92)', color: '#F3F2EC', fontFamily: FONT_SANS, animation: 'hvFade 0.18s ease both' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, height: 64, padding: '0 20px', flex: 'none' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.originalName}</div>
                    <div style={{ marginTop: 2, fontFamily: FONT_SANS, fontSize: 10.5, letterSpacing: '0.06em', opacity: 0.6, whiteSpace: 'pre' }}>
                        {[kindWord(a).toUpperCase(), fmtBytes(a.bytes).toUpperCase(), a.width && a.height ? `${a.width}×${a.height}` : ''].filter(Boolean).join('  ·  ')}
                    </div>
                </div>
                <a href={a.url} target="_blank" rel="noopener noreferrer" download={a.originalName} aria-label="Download" className="hv-pv-btn" style={pvBtn}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 4v11M7 10.5l5 5 5-5M5 19.5h14" /></svg>
                </a>
                <button type="button" onClick={() => onDelete(a)} aria-label="Delete" className="hv-pv-btn" style={pvBtn}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4.5 7h15M10 11v6M14 11v6M6.5 7l.8 11a2 2 0 0 0 2 1.9h5.4a2 2 0 0 0 2-1.9l.8-11M9.5 7V5.5a1.5 1.5 0 0 1 1.5-1.5h2a1.5 1.5 0 0 1 1.5 1.5V7" /></svg>
                </button>
                <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.14)' }} />
                <button type="button" onClick={onClose} aria-label="Close preview" className="hv-pv-btn" style={pvBtn}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', gap: 16, padding: '8px 20px 20px' }}>
                <button type="button" onClick={() => go(-1)} aria-label="Previous file" className="hv-pv-btn" style={{ ...pvBtn, width: 44, height: 44 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
                </button>
                <div onClick={onClose} style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {a.kind === 'video' ? (
                        // key: a new src must reload the element, not keep playing the previous file.
                        <video key={a.assetId} src={a.url} controls autoPlay playsInline onClick={(e) => e.stopPropagation()} style={{ maxWidth: 'min(1100px,100%)', maxHeight: '100%', borderRadius: 6, background: '#000' }} />
                    ) : (
                        <div onClick={(e) => e.stopPropagation()} role="img" aria-label={a.alt || a.originalName} style={{ width: '100%', height: '100%', maxWidth: 1100, backgroundImage: `url("${a.url}")`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
                    )}
                </div>
                <button type="button" onClick={() => go(1)} aria-label="Next file" className="hv-pv-btn" style={{ ...pvBtn, width: 44, height: 44 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                </button>
            </div>
            <div style={{ flex: 'none', padding: '0 0 18px', textAlign: 'center', fontFamily: FONT_SANS, fontSize: 11, letterSpacing: '0.06em', opacity: 0.55 }}>{index + 1} / {list.length}</div>
        </div>
    );
};

/** Design list pager ("Showing 1–8 of 20", prev / page select / next). */
const Pager: React.FC<{ page: number; total: number; onPage: (p: number) => void }> = ({ page, total, onPage }) => {
    const pages = Math.max(1, Math.ceil(total / PAGE));
    const from = total ? page * PAGE + 1 : 0, to = Math.min(total, (page + 1) * PAGE);
    const nav = (dis: boolean): React.CSSProperties => ({ width: 32, height: 32, borderRadius: 8, border: 0, background: 'transparent', color: 'var(--hv-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: dis ? 'default' : 'pointer', opacity: dis ? 0.4 : 1 });
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '14px 4px 0' }}>
            <span style={{ fontSize: 12.5, color: 'var(--hv-muted)' }}>{total ? `Showing ${from}–${to} of ${total}` : 'No results'}</span>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button type="button" className="hv-iconbtn" aria-label="Previous page" disabled={page === 0} onClick={() => onPage(page - 1)} style={nav(page === 0)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
                </button>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 6px', fontSize: 12.5, color: 'var(--hv-muted)' }}>
                    Page
                    <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <select value={page} onChange={(e) => onPage(parseInt(e.target.value, 10) || 0)} aria-label="Go to page" style={{ appearance: 'none', WebkitAppearance: 'none', height: 32, padding: '0 28px 0 12px', borderRadius: 6, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 12.5, cursor: 'pointer', outline: 'none' }}>
                            {Array.from({ length: pages }, (_, i) => <option key={i} value={i}>{i + 1}</option>)}
                        </select>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', right: 9, pointerEvents: 'none', color: 'var(--hv-muted)' }} aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                    </span>
                    of {pages}
                </label>
                <button type="button" className="hv-iconbtn" aria-label="Next page" disabled={page >= pages - 1} onClick={() => onPage(page + 1)} style={nav(page >= pages - 1)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                </button>
            </div>
        </div>
    );
};

export const DashMedia: React.FC = () => {
    const dash = useDash();
    const confirm = useConfirm();
    const fileRef = useRef<HTMLInputElement>(null);
    const [assets, setAssets] = useState<MediaAsset[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [enabled, setEnabled] = useState(true);
    const [usage, setUsage] = useState<{ used: number; quota: number } | null>(null);
    const [kind, setKind] = useState<KindFilter>('all');
    const [query, setQuery] = useState('');
    const [layout, setLayoutState] = useState<Layout>(readLayout);
    const [page, setPage] = useState(0);
    const [menuFor, setMenuFor] = useState<string | null>(null);
    const [previewIdx, setPreviewIdx] = useState<number | null>(null);
    const [uploads, setUploads] = useState<Upload[]>([]);
    const uploadSeq = useRef(0);

    const setLayout = (v: Layout) => {
        setLayoutState(v);
        try { localStorage.setItem(LAYOUT_KEY, v); } catch { /* private mode */ }
    };

    const load = useCallback(async () => {
        const r = await mediaService.list();
        if (r.success && r.data) {
            setAssets(r.data.assets);
            setEnabled(r.data.enabled !== false);
            setUsage(r.data.quotaBytes ? { used: r.data.usedBytes, quota: r.data.quotaBytes } : null);
            setLoadError(null);
        } else {
            setLoadError(errMsg(r) || 'Could not load your media.');
        }
        setLoading(false);
    }, []);
    useEffect(() => { void load(); }, [load]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return assets.filter((a) => (kind === 'all' || a.kind === kind) && (!q || a.originalName.toLowerCase().includes(q)));
    }, [assets, kind, query]);
    const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
    const curPage = Math.min(page, pages - 1);
    const paged = filtered.slice(curPage * PAGE, (curPage + 1) * PAGE);

    // Files upload one at a time so the per-file bars are honest and a 200 MB video
    // does not starve the others.
    const uploadFiles = async (files: File[]) => {
        const ok = files.filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
        if (ok.length < files.length) dash.notify('warn', 'Only images and videos can be uploaded.');
        if (!ok.length) return;
        const queued = ok.map((f) => ({ file: f, up: { id: ++uploadSeq.current, name: f.name, progress: 0 } }));
        setUploads((u) => [...u, ...queued.map((q) => q.up)]);
        let done = 0;
        for (const { file, up } of queued) {
            const r = await mediaService.upload(file, { onProgress: (p) => setUploads((u) => u.map((x) => (x.id === up.id ? { ...x, progress: p } : x))) });
            setUploads((u) => u.filter((x) => x.id !== up.id));
            if (r.success && r.data) { done++; setAssets((as) => [r.data as MediaAsset, ...as]); }
            else dash.notify('error', `“${file.name}” could not be uploaded${errMsg(r) ? `: ${errMsg(r)}` : '.'}`);
        }
        if (done) {
            dash.notify('success', `${done} ${done > 1 ? 'files' : 'file'} uploaded`);
            void load(); // refresh quota usage
        }
    };
    const onFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        void uploadFiles(files);
    };

    const copyLink = async (a: MediaAsset) => {
        try {
            await navigator.clipboard.writeText(absUrl(a.url));
            dash.notify('success', 'Link copied');
        } catch {
            dash.notify('error', 'Could not copy the link.');
        }
    };

    const del = async (a: MediaAsset) => {
        const ok = await confirm({
            title: `Delete “${a.originalName}”?`,
            description: 'This removes the file from your library. Sites that already use it may show a broken image.',
            confirmLabel: 'Delete',
            onConfirm: async () => {
                const r = await mediaService.remove(a.assetId);
                if (!r.success) throw new Error(r.message || 'Could not delete the file.');
            },
        });
        if (!ok) return;
        setAssets((as) => as.filter((x) => x.assetId !== a.assetId));
        dash.notify('success', `Deleted “${a.originalName}”`);
        void load();
    };

    const openPreview = (a: MediaAsset) => { setMenuFor(null); setPreviewIdx(filtered.findIndex((x) => x.assetId === a.assetId)); };
    const toggleMenu = (e: React.MouseEvent, id: string) => { e.stopPropagation(); setMenuFor((m) => (m === id ? null : id)); };
    const renderMenu = (a: MediaAsset, style: React.CSSProperties) => menuFor === a.assetId && (
        <FileMenu a={a} style={style} onClose={() => setMenuFor(null)} onPreview={() => openPreview(a)} onCopy={() => void copyLink(a)} onDelete={() => void del(a)} />
    );

    const lead = (
        <>
            Photos, logos and files you can reuse in any site or video.
            {usage && <span style={{ color: 'var(--hv-muted)' }}>{` ${fmtBytes(usage.used)} of ${fmtBytes(usage.quota)} used.`}</span>}
        </>
    );

    return (
        <section data-screen-label="Media" style={{ display: 'flex', flexDirection: 'column', gap: 32, animation: 'hvRise 0.4s cubic-bezier(0.22,0.9,0.24,1) both' }}>
            <style>{'.hv-dash .hv-pv-btn:hover { background: rgba(255,255,255,0.16) !important; }'}</style>
            <PageHead title="Media" lead={lead}>
                <FillButton
                    onClick={() => fileRef.current?.click()}
                    disabled={!enabled}
                    data-track="dash_media_upload"
                    icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 15.5V4M7.5 8.5 12 4l4.5 4.5M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" /></svg>}
                >
                    Upload
                </FillButton>
                <input ref={fileRef} type="file" multiple accept="image/*,video/*" onChange={onFiles} style={{ display: 'none' }} />
            </PageHead>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: -6 }}>
                <FilterDropdown label="Type" value={kind} options={KIND_OPTS} onChange={(v) => { setKind(v); setPage(0); }} />
                <div style={{ flex: 1 }} />
                <ViewToggle value={layout} onChange={setLayout} />
                <SearchBox value={query} onChange={(v) => { setQuery(v); setPage(0); }} placeholder="Search files" />
            </div>

            {uploads.length > 0 && (
                <div role="status" aria-live="polite" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 16px', borderRadius: 8, border: '1px solid var(--hv-line)', background: 'var(--hv-card)' }}>
                    {uploads.map((u) => (
                        <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5 }}>
                            <span style={{ flex: '0 1 240px', minWidth: 0, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</span>
                            <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'var(--hv-page-2)', overflow: 'hidden' }}>
                                <div style={{ width: `${Math.round(u.progress * 100)}%`, height: '100%', borderRadius: 999, background: 'var(--brand,#13422C)', transition: 'width 0.2s ease' }} />
                            </div>
                            <span style={{ flex: 'none', width: 70, textAlign: 'right', fontFamily: FONT_SANS, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>
                                {u.progress >= 1 ? 'PROCESSING' : u.progress > 0 ? `${Math.round(u.progress * 100)}%` : 'WAITING'}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <div style={{ padding: '24px 0', fontFamily: FONT_SANS, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>LOADING…</div>
            ) : loadError ? (
                <div style={{ padding: '64px 24px', textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_SERIF, fontSize: 26, letterSpacing: '0.01em', wordSpacing: '0.06em' }}>Could not load your media</div>
                    <div style={{ marginTop: 6, fontSize: 13.5, color: 'var(--hv-body)' }}>{loadError}</div>
                    <button type="button" className="hv-btn-out" onClick={() => { setLoading(true); void load(); }} style={{ margin: '16px auto 0', height: 34, padding: '0 14px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Try again</button>
                </div>
            ) : filtered.length === 0 ? (
                <div style={{ padding: '64px 24px', textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_SERIF, fontSize: 26, letterSpacing: '0.01em', wordSpacing: '0.06em' }}>{assets.length ? 'No files found' : !enabled ? 'Media is not available' : 'No files yet'}</div>
                    <div style={{ marginTop: 6, fontSize: 13.5, color: 'var(--hv-body)' }}>
                        {assets.length ? 'Try another name or file type.' : !enabled ? 'The media library is turned off for this workspace.' : 'Upload photos, logos or clips to reuse them in any site or video.'}
                    </div>
                </div>
            ) : layout === 'grid' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 14 }}>
                    {filtered.map((a) => (
                        <div key={a.assetId} className="hv-card" role="button" tabIndex={0} onClick={() => openPreview(a)} onKeyDown={(e) => { if (e.key === 'Enter') openPreview(a); }} style={{ position: 'relative', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
                            <div className="hv-thumb" style={{ position: 'relative', height: 130, borderRadius: 8, border: '1px solid var(--hv-line)', background: 'var(--hv-page-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--hv-muted)', overflow: 'hidden' }}>
                                {a.kind === 'image' ? (
                                    <div role="img" aria-label={a.alt || a.originalName} style={{ position: 'absolute', inset: 0, backgroundImage: `url("${a.url}")`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                                ) : (
                                    <>
                                        <video src={a.url} preload="metadata" muted playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
                                        <span style={{ position: 'relative', width: 34, height: 34, borderRadius: 999, background: 'rgba(14,42,28,0.55)', color: '#FFFDF7', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 6.6c0-1.2 1.3-1.9 2.3-1.3l8.4 5.4c.9.6.9 2 0 2.6l-8.4 5.4c-1 .6-2.3-.1-2.3-1.3z" /></svg>
                                        </span>
                                    </>
                                )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '10px 0 12px 12px' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="hv-cardtitle" style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.originalName}</div>
                                    <div style={{ marginTop: 3, fontFamily: FONT_SANS, fontSize: 9, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>{metaOf(a)}</div>
                                </div>
                                <button type="button" data-dismiss-ignore aria-label="File actions" className="hv-iconbtn" onClick={(e) => toggleMenu(e, a.assetId)} style={{ ...dotsBtn, width: 28, height: 28, marginTop: -2 }}>
                                    <Dots />
                                </button>
                            </div>
                            {renderMenu(a, { right: 0, top: 'calc(100% - 8px)' })}
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="hv-lib-row" style={{ display: 'grid', gap: 24, alignItems: 'center', padding: '0 20px', height: 40, background: 'var(--hv-page-2)', borderTop: '1px solid var(--hv-line)', borderBottom: '1px solid var(--hv-line)', fontSize: 12, fontWeight: 600, color: 'var(--hv-body)', gridTemplateColumns: listCols }}>
                        <span>Name</span>
                        <span className="hv-db-hide-sm">Type</span>
                        <span>Size</span>
                        <span />
                    </div>
                    {paged.map((a) => (
                        <div key={a.assetId} className="hv-lib-row" role="button" tabIndex={0} onClick={() => openPreview(a)} onKeyDown={(e) => { if (e.key === 'Enter') openPreview(a); }} style={{ position: 'relative', display: 'grid', gap: 24, alignItems: 'center', padding: '0 20px', minHeight: 48, borderBottom: '1px solid var(--hv-line)', cursor: 'pointer', fontSize: 13, gridTemplateColumns: listCols }}>
                            <span className="hv-rowname" style={{ justifySelf: 'start', maxWidth: '100%', minWidth: 0, padding: '4px 0', margin: '-4px 0', color: 'var(--hv-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.originalName}</span>
                            <span className="hv-db-hide-sm" style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--hv-ink)' }}>
                                <Icon d={a.kind === 'video' ? ICON.video : ICON.image} size={14} sw={2} style={{ flex: 'none' }} />
                                {kindWord(a)}
                            </span>
                            <span style={{ color: 'var(--hv-ink)', whiteSpace: 'nowrap' }}>{fmtBytes(a.bytes)}</span>
                            <button type="button" data-dismiss-ignore aria-label="File actions" className="hv-iconbtn" onClick={(e) => toggleMenu(e, a.assetId)} style={{ ...dotsBtn, width: 30, height: 30 }}>
                                <Dots />
                            </button>
                            {renderMenu(a, { right: 14, top: 'calc(100% - 6px)' })}
                        </div>
                    ))}
                    <Pager page={curPage} total={filtered.length} onPage={setPage} />
                </div>
            )}

            {previewIdx !== null && previewIdx >= 0 && filtered[previewIdx] && (
                <Preview list={filtered} index={previewIdx} onGo={setPreviewIdx} onClose={() => setPreviewIdx(null)} onDelete={(a) => { setPreviewIdx(null); void del(a); }} />
            )}
        </section>
    );
};

export default DashMedia;
